import { Reward, RewardRarity } from "./types";

/** Active pool — points only for now (coupons can be re-enabled later) */
export const REWARD_POOL: Reward[] = [
  { type: "points", value: 50, rarity: "common", label: "50 Points", icon: "⭐" },
  { type: "points", value: 100, rarity: "common", label: "100 Points", icon: "⭐" },
  { type: "points", value: 150, rarity: "common", label: "150 Points", icon: "⭐" },
  { type: "points", value: 200, rarity: "common", label: "200 Points", icon: "⭐" },
  { type: "points", value: 250, rarity: "rare", label: "250 Points", icon: "💎" },
  { type: "points", value: 400, rarity: "rare", label: "400 Points", icon: "💎" },
  { type: "points", value: 500, rarity: "epic", label: "500 Points", icon: "✨" },
  { type: "points", value: 750, rarity: "epic", label: "750 Points", icon: "✨" },
  { type: "points", value: 1000, rarity: "legendary", label: "1000 Points", icon: "👑" },
  { type: "points", value: 2500, rarity: "legendary", label: "2500 Points", icon: "👑" },
];

const ACTIVE_REWARD_POOL = REWARD_POOL.filter((r) => r.type === "points");

const RARITY_WEIGHTS: Record<RewardRarity, number> = {
  common: 60,
  rare: 25,
  epic: 10,
  legendary: 5,
};

export function generateWeightedReward(): Reward {
  const roll = Math.random() * 100;
  let cumulative = 0;
  let chosenRarity: RewardRarity = "common";

  for (const [rarity, weight] of Object.entries(RARITY_WEIGHTS) as [
    RewardRarity,
    number,
  ][]) {
    cumulative += weight;
    if (roll <= cumulative) {
      chosenRarity = rarity;
      break;
    }
  }

  const pool = ACTIVE_REWARD_POOL.filter((r) => r.rarity === chosenRarity);
  return pool[Math.floor(Math.random() * pool.length)] ?? ACTIVE_REWARD_POOL[0];
}

export function generateOtherBoxRewards(
  excludeReward: Reward,
  count: number
): Reward[] {
  const others: Reward[] = [];
  for (let i = 0; i < count; i++) {
    let reward = generateWeightedReward();
    let attempts = 0;
    while (
      reward.label === excludeReward.label &&
      attempts < 10
    ) {
      reward = generateWeightedReward();
      attempts++;
    }
    others.push(reward);
  }
  return others;
}

export function getRarityGlow(rarity: RewardRarity): string {
  switch (rarity) {
    case "rare":
      return "shadow-[0_0_40px_rgba(59,130,246,0.6)] ring-blue-400/50";
    case "epic":
      return "shadow-[0_0_40px_rgba(168,85,247,0.7)] ring-purple-400/50";
    case "legendary":
      return "shadow-[0_0_50px_rgba(251,191,36,0.8)] ring-amber-400/60";
    default:
      return "shadow-[0_0_24px_rgba(148,163,184,0.4)] ring-slate-400/30";
  }
}

export function getRarityBadgeClass(rarity: RewardRarity): string {
  switch (rarity) {
    case "rare":
      return "bg-blue-500/20 text-blue-200 border-blue-400/40";
    case "epic":
      return "bg-purple-500/20 text-purple-200 border-purple-400/40";
    case "legendary":
      return "bg-amber-500/20 text-amber-200 border-amber-400/40";
    default:
      return "bg-slate-500/20 text-slate-200 border-slate-400/40";
  }
}

export function formatRewardValue(reward: Reward): string {
  if (reward.type === "points") {
    return `${reward.value} Points`;
  }
  return String(reward.value);
}

export function getRewardTypeLabel(reward: Reward): string {
  return reward.type === "points" ? "Points" : "Coupon";
}

/** Placeholder — wire confetti when integrating celebrations */
export function triggerConfettiPlaceholder(): void {}

/** Placeholder — wire sound effects */
export function triggerSoundPlaceholder(_event: string): void {}

/** Placeholder — wire analytics */
export function trackAnalyticsEvent(
  _event: string,
  _payload?: Record<string, unknown>
): void {}
